import { tradesmen } from '../api';
import { createPageHeader } from '../components/form';
import { createDataTable, type Column } from '../components/table';
import { showModal, confirmModal } from '../components/modal';
import { showToast } from '../components/toast';
import type { Tradesman } from '../types';

export async function tradesmenPage(): Promise<HTMLElement> {
  const page = document.createElement('div');

  const addBtn = document.createElement('button');
  addBtn.className = 'btn-primary';
  addBtn.textContent = '+ Add Tradesman';
  addBtn.addEventListener('click', () => showTradesmanModal(null, reload));
  page.appendChild(createPageHeader('Tradesmen', addBtn));

  const tableContainer = document.createElement('div');
  page.appendChild(tableContainer);

  const reload = async () => {
    const list = await tradesmen.list();
    renderTable(tableContainer, list, reload);
  };
  await reload();
  return page;
}

function renderTable(container: HTMLElement, list: Tradesman[], reload: () => Promise<void>): void {
  container.innerHTML = '';
  const cols: Column<Tradesman>[] = [
    { label: 'Name', render: r => `<a href="#/tradesmen/${r.id}" class="text-primary-600 hover:underline font-medium">${r.name}</a>` },
    { label: 'Trade', render: r => `<span class="badge badge-blue">${r.trade}</span>` },
    { label: 'Company', key: 'company' },
    { label: 'Phone', key: 'phone' },
    { label: 'Email', key: 'email' },
    { label: 'ABN', key: 'abn' },
    {
      label: 'Actions',
      render: r => `
        <button class="text-primary-600 hover:underline text-sm edit-btn" data-id="${r.id}">Edit</button>
        <button class="text-red-500 hover:underline text-sm ml-2 del-btn" data-id="${r.id}">Delete</button>
      `,
    },
  ];
  const table = createDataTable(cols, list, { emptyText: 'No tradesmen added yet.' });
  container.appendChild(table);

  table.querySelectorAll('.edit-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = list.find(x => x.id === btn.getAttribute('data-id'));
      if (t) showTradesmanModal(t, reload);
    });
  });
  table.querySelectorAll('.del-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (await confirmModal('Delete this tradesman?')) {
        try {
          await tradesmen.delete(btn.getAttribute('data-id')!);
          showToast('Deleted', 'success');
          await reload();
        } catch { showToast('Failed to delete', 'error'); }
      }
    });
  });
}

function showTradesmanModal(existing: Tradesman | null, onSave: () => Promise<void>): void {
  const form = document.createElement('form');
  form.className = 'space-y-4';
  form.innerHTML = `
    <div><label class="label">Name *</label><input name="name" type="text" class="input-field" required value="${existing?.name || ''}"></div>
    <div><label class="label">Trade *</label><input name="trade" type="text" class="input-field" required value="${existing?.trade || ''}" placeholder="e.g. Plumber, Electrician"></div>
    <div><label class="label">Company</label><input name="company" type="text" class="input-field" value="${existing?.company || ''}"></div>
    <div><label class="label">Phone</label><input name="phone" type="tel" class="input-field" value="${existing?.phone || ''}"></div>
    <div><label class="label">Email</label><input name="email" type="email" class="input-field" value="${existing?.email || ''}"></div>
    <div><label class="label">ABN</label><input name="abn" type="text" class="input-field" value="${existing?.abn || ''}" placeholder="Australian Business Number"></div>
    <div><label class="label">Notes</label><textarea name="notes" class="input-field" rows="2">${existing?.notes || ''}</textarea></div>
    <div class="flex justify-end gap-3">
      <button type="button" class="btn-outline cancel-btn">Cancel</button>
      <button type="submit" class="btn-primary">${existing ? 'Update' : 'Add'}</button>
    </div>
  `;

  const { close } = showModal(existing ? 'Edit Tradesman' : 'Add Tradesman', form);
  form.querySelector('.cancel-btn')!.addEventListener('click', close);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const data = Object.fromEntries(fd.entries());
    try {
      if (existing) {
        await tradesmen.update(existing.id, data);
      } else {
        await tradesmen.create(data);
      }
      close();
      showToast(existing ? 'Updated' : 'Added', 'success');
      await onSave();
    } catch { showToast('Failed to save', 'error'); }
  });
}
